import React from 'react';
import {
  Box, Breadcrumbs, Button, CircularProgress, Collapse, Container,
  Dialog, DialogContent, IconButton, Link as MuiLink, Slide, Snackbar,
  Tooltip, Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import AlertTitle from '@mui/material/AlertTitle';
import CloseIcon from '@mui/icons-material/Close';

import ScreenerTablePro from './ScreenerTablePro';
import QueryBuilder from './QueryBuilder';
import ScreenPresets from './ScreenPresets';
import SaveScreener from './SaveScreener';
import RenameScreener from './RenameScreener';
import ManageColumnsDialog from './ManageTableColumns.js';
import DownloadTableData from './DownloadTableData/index.js';
import PremiumBanner from '../Pricing/PremiumBanner';
import QueryExamples from './QueryExamples/index.js';
import QuickFilterBar from './QuickFilterBar';
import { options, DEFAULT_TBL_COLS, mainHeadCols, defaultCols, userHasPresets } from '../../hook/useScreenerState';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Shared layout shell for all 3 screener pages.
 *
 * @param {Object} props
 * @param {Object} props.state       – Everything returned from useScreenerState()
 * @param {Object} props.pageProps   – Original page props (hero, pageLoading, etc.)
 * @param {Array}  props.breadcrumbs – Array of { label, href? } for breadcrumbs
 * @param {string} props.pageTitle   – h1 text
 * @param {string} props.pageDesc    – description text below h1
 * @param {'index'|'preset'|'user-preset'} props.mode
 */
export default function ScreenerLayout({ state, pageProps = {}, breadcrumbs = [], pageTitle, pageDesc, mode = 'index' }) {
  const {
    // Auth
    user, isPremium,
    // Data
    screenerMetrics, estimatedResults, showingResults, inputText,
    // Presets
    userPresets, userUpdatedPreset, userDeletedPreset,
    activePresetName, activePresetDesc, activePresetCode,
    presetCriteria,
    // Table
    tableCols, columnGroups, offset,
    // UI
    isLoading, isTableLoading, saveLoading, message, banner, renameDialog,
    // Mode-specific
    screenerNotFound, watchlists,
    // Setters
    setFormula, setTableCols, setPresetCriteria,
    setActivePresetName, setActivePresetDesc, setActivePresetCode,
    setBanner, setOffset, setUserUpdatedPreset, setUserDeletedPreset,
    setColumnGroups, setScreenerNotFound,
    // Handlers
    handleNewMetrics, updatePreset, renamePreset, deletePreset,
    handleDownload, onSortEnd, runQuery,
    handleRenameOpen, handleRenameClose, handleBannerClose, handleExited,
    logoutUser, handleNewScreen, tryThisQuery,
  } = state;

  const { pageLoading } = pageProps;

  return (
    <Container maxWidth="xl" sx={{ pb: 6 }}>
      {/* Breadcrumb */}
      <Box component="nav" sx={{ pt: { xs: 1.5, sm: 2 }, pb: 0.5 }}>
        <Breadcrumbs
          separator={<NavigateNextRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
          aria-label="breadcrumb"
        >
          <MuiLink
            underline="hover"
            href="/"
            sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1, '&:hover': { color: 'primary.main' } }}
          >
            Home
          </MuiLink>
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return isLast ? (
              <Typography key={crumb.label} sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 500, lineHeight: 1 }}>
                {crumb.label}
              </Typography>
            ) : (
              <MuiLink
                key={crumb.label}
                underline="hover"
                href={crumb.href}
                sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1, '&:hover': { color: 'primary.main' } }}
              >
                {crumb.label}
              </MuiLink>
            );
          })}
        </Breadcrumbs>
      </Box>

      {/* Page Header */}
      <Box sx={{ pt: 1, pb: { xs: 2, sm: 2.5 }, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              fontWeight: 700,
              color: 'text.primary',
              letterSpacing: '-0.01em',
              lineHeight: 1.3,
            }}
          >
            {pageTitle}
          </Typography>
          {pageDesc && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, fontSize: '0.9rem', color: 'rgb(100, 116, 139)', lineHeight: 1.5, maxWidth: 560 }}
            >
              {pageDesc}
            </Typography>
          )}
        </Box>
        <Button
          component="a"
          href="#build-query"
          data-event-name="new-screen"
          data-event-category="stock-screener"
          data-event-label="new-query"
          variant="outlined"
          size="small"
          disableElevation
          startIcon={<AddRoundedIcon sx={{ pointerEvents: 'none' }} />}
          sx={{
            textTransform: 'none',
            fontSize: '0.85rem',
            borderRadius: '10px',
            '& .MuiButton-startIcon': { pointerEvents: 'none' },
            mt: { xs: 0, sm: 0.5 },
          }}
          onClick={handleNewScreen}
        >
          New Screen
        </Button>
      </Box>

      {/* Screen Presets */}
      <ScreenPresets
        setFormula={setFormula}
        setBanner={setBanner}
        deletePreset={deletePreset}
        renamePreset={renamePreset}
        setTableCols={setTableCols}
        setPresetCriteria={setPresetCriteria}
        setActivePresetName={setActivePresetName}
        setActivePresetDesc={setActivePresetDesc}
        setActivePresetCode={setActivePresetCode}
        presetCriteria={presetCriteria}
        userPresets={userPresets}
        activePresetName={activePresetName}
        userDeletedPreset={userDeletedPreset}
        userUpdatedPreset={userUpdatedPreset}
        defaultScreenerTableCols={DEFAULT_TBL_COLS}
        isPremium={isPremium}
      />

      {/* Quick Filter Bar */}
      <QuickFilterBar inputText={inputText} runQuery={runQuery} options={options} tableCols={tableCols} setTableCols={setTableCols} />

      {/* Query Builder Section */}
      <Box sx={{ mb: 3, minHeight: '60vh', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px', p: { xs: 2, md: 3 }, bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            id="build-query"
            sx={{
              lineHeight: 1.2,
              fontSize: '1.05rem',
              fontWeight: activePresetName ? 600 : 500,
              scrollMarginTop: 'calc(56px + 26px)',
              mt: 2,
              textTransform: activePresetName ? 'capitalize' : 'none',
            }}
          >
            {activePresetName || 'Build or customize your query below'}:
            {user?.accessToken && activePresetName && (
              <Tooltip title="Edit screen name or description" placement="right" disableInteractive arrow>
                <IconButton
                  onClick={() => handleRenameOpen()}
                  size="small"
                  disabled={userPresets?.find(item => item?.code === activePresetCode)?.isSystemDefault || false}
                  sx={{ ml: 0.5 }}
                >
                  <DriveFileRenameOutlineRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          {activePresetDesc && (
            <Typography variant="body2" sx={{ mt: 0.5, color: 'rgb(100, 116, 139)' }}>
              {activePresetDesc}
            </Typography>
          )}
        </Box>

        {/* User-preset not-found alert */}
        {mode === 'user-preset' && !estimatedResults && !(screenerMetrics?.length > 0) && !isLoading && screenerNotFound && (
          <MuiAlert
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setScreenerNotFound(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 3, borderRadius: '8px' }}
            severity="error"
            color="error"
          >
            <AlertTitle>Error</AlertTitle>
            Couldn&apos;t load this screen. It doesn&apos;t exist or was deleted.
          </MuiAlert>
        )}

        <QueryBuilder
          options={options}
          value={inputText}
          setFormula={setFormula}
          errorMessage={screenerMetrics?.err?.message}
          estimatedResults={estimatedResults}
          isLoading={isTableLoading}
          handleNewMetrics={handleNewMetrics}
          {...(mode === 'user-preset' && {
            presetCriteria: presetCriteria?.replace(/`([^`]+)`/g, (match, p1) => {
              const replacement = options.find(item => item.aggCode === p1);
              return replacement ? replacement.shortName : match;
            }),
            screenerNotFound,
            queryExamples: <QueryExamples tryThisQuery={tryThisQuery} />,
          })}
          matchedResults={
            <Box sx={{ ml: { md: 'auto', sm: 0, xs: 0 } }}>
              <Box sx={{ px: { md: 3, sm: 0, xs: 0 }, height: '100%', display: { md: 'block', sm: 'flex', xs: 'flex' }, alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'rgb(100, 116, 139)', fontSize: '.875rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                  Matching Stocks
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { md: '3rem', sm: '.875rem', xs: '.875rem' }, textAlign: 'right', lineHeight: 1, ml: { xs: 1, md: 0, sm: 1 } }}>
                  {isLoading
                    ? <CircularProgress color="primary" sx={{ height: { md: '40px !important', sm: '15px !important', xs: '15px !important' }, width: { md: '40px !important', sm: '15px !important', xs: '15px !important' } }} />
                    : (estimatedResults ?? 0)}
                </Typography>
              </Box>
            </Box>
          }
          saveScreener={
            <SaveScreener
              updatePreset={updatePreset}
              isPremium={isPremium}
              saveLoading={saveLoading}
              setBanner={setBanner}
              userUpdatedPreset={userUpdatedPreset}
              activePresetName={activePresetName}
              activePresetDesc={activePresetDesc}
              userHasPresets={userHasPresets(userPresets)}
              user={user}
            />
          }
        />

        {/* Results Table */}
        <Collapse
          style={{ transformOrigin: '0 0 0' }}
          {...(estimatedResults ? { timeout: 1000 } : {})}
          in={estimatedResults}
        >
          <Box sx={{ borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
            <ScreenerTablePro
              rows={screenerMetrics ? screenerMetrics : []}
              headCells={tableCols}
              setTableCols={setTableCols}
              defaultHeadCells={tableCols}
              wrapperClass={'screener-table'}
              shortWrapperClass={'screener-field'}
              quickSearch={true}
              needToolbar={true}
              fullToolbarAccess={false}
              createSections={false}
              noOfRows={25}
              estimatedResults={estimatedResults ?? 0}
              isLoading={isTableLoading}
              setOffset={setOffset}
              offset={offset}
              mounted={false}
              isPremium={isPremium}
              setBanner={setBanner}
              {...(mode !== 'index' && { user, _watchlists: watchlists })}
              manageColumns={
                <ManageColumnsDialog
                  arrayOrder={mainHeadCols}
                  columns={tableCols}
                  setColumnGroups={setColumnGroups}
                  setColumns={setTableCols}
                  onSortEnd={onSortEnd}
                />
              }
              download={
                <DownloadTableData
                  handleDownload={handleDownload}
                  user={user?.accessToken}
                  message={message}
                  logout={logoutUser}
                  isPremium={isPremium}
                />
              }
              tableName={
                <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'center', '&:hover .rename-icon': { color: 'primary.main' } }}>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, ml: 2, mr: 0.5, textTransform: 'capitalize' }}>
                    {activePresetName}
                  </Typography>
                  {user?.accessToken && (
                    <IconButton size="small" onClick={() => handleRenameOpen()}>
                      <DriveFileRenameOutlineRoundedIcon className="rename-icon" sx={{ color: 'text.disabled', fontSize: '1rem' }} />
                    </IconButton>
                  )}
                </Box>
              }
              results={showingResults
                ? <Typography variant="body2" sx={{ fontSize: '.82rem', fontWeight: 500, ml: 2, mr: 'auto', color: 'rgb(100, 116, 139)', display: { xs: 'none', sm: 'block' } }}>
                    {estimatedResults} result{estimatedResults <= 1 ? '' : 's'} found
                  </Typography>
                : ''}
              activePresetName={activePresetName ?? ""}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Premium Dialog */}
      <Dialog
        open={banner && !pageLoading}
        onClose={handleBannerClose}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="auth-dialog"
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: 'calc(100% - 64px)',
            width: '100% !important',
            bgcolor: 'transparent',
            p: '0 !important',
            boxShadow: 0,
            textAlign: 'center',
          },
          '& .MuiDialogContent-root': { p: 0 },
          '& .MuiBackdrop-root': { backdropFilter: 'blur(3px)' },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <PremiumBanner maxWidth={12} {...pageProps} isPopup={true} user={user?.accessToken} message={message} screenerBanner={banner} logout={logoutUser} />
        </DialogContent>
      </Dialog>

      {/* Notifications */}
      <Snackbar TransitionProps={{ onExited: handleExited }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={userUpdatedPreset === undefined} autoHideDuration={2000} onClose={() => setUserUpdatedPreset(null)}>
        <Alert onClose={() => setUserUpdatedPreset(null)} severity="error" sx={{ width: '100%' }}>
          Error updating your preset. Please try again later!
        </Alert>
      </Snackbar>
      <Snackbar TransitionProps={{ onExited: handleExited }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={userUpdatedPreset} autoHideDuration={2000} onClose={() => setUserUpdatedPreset(null)}>
        <Alert onClose={() => setUserUpdatedPreset(null)} severity="success" sx={{ width: '100%' }}>
          {userUpdatedPreset} updated successfully!
        </Alert>
      </Snackbar>
      <Snackbar TransitionProps={{ onExited: handleExited }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={userDeletedPreset} autoHideDuration={2000} onClose={() => setUserDeletedPreset(null)}>
        <Alert onClose={() => setUserDeletedPreset(null)} severity="success" sx={{ width: '100%' }}>
          Deleted successfully!
        </Alert>
      </Snackbar>

      <RenameScreener
        renamePreset={renamePreset}
        renameDialog={renameDialog}
        handleRenameClose={handleRenameClose}
        userUpdatedPreset={userUpdatedPreset}
        activePresetName={activePresetName}
        activePresetDesc={activePresetDesc}
        activePresetCode={activePresetCode}
        isPremium={isPremium}
        setBanner={setBanner}
      />
    </Container>
  );
}
